import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";

const tiers = [
  {
    name: "Launch",
    priceUsd: 2400,
    tagline: "Get online, fast — with a foundation built for growth.",
    features: [
      "5-page conversion-focused website",
      "SEO-ready page structure",
      "Mobile-first design system",
      "Analytics + Search Console setup",
      "2 rounds of revisions",
    ],
    cta: "Start with Launch",
  },
  {
    name: "Growth",
    priceUsd: 5800,
    tagline: "Design, SEO, and content working together — our most popular package.",
    features: [
      "10-page site + landing page",
      "Full technical + on-page SEO",
      "Content strategy & briefs (6 topics)",
      "Branding refresh & style guide",
      "Analytics dashboard",
      "30 days post-launch support",
    ],
    cta: "Choose Growth",
    highlighted: true,
  },
  {
    name: "Apex Pro",
    priceUsd: null,
    priceLabel: "Custom",
    tagline: "A dedicated growth partner — for teams ready to scale seriously.",
    features: [
      "Custom site + funnel architecture",
      "Advanced SEO program",
      "Monthly experiments (A/B, CRO)",
      "Content production (4/mo)",
      "Weekly strategy calls",
      "Quarterly business reviews",
    ],
    cta: "Talk to us",
  },
];

export default function Pricing() {
  const { format, currency } = useCurrency();

  return (
    <section id="pricing" data-testid="pricing-section" className="section-pad relative">
      <div className="container-apex">
        <div className="max-w-3xl">
          <div className="eyebrow mb-4"><span className="w-6 h-px bg-apex-cyan" /> APEX Innovation Solutions · Packages</div>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-[1.05]">
            Simple, honest pricing — <span className="text-apex-orange">no bloat.</span>
          </h2>
          <p className="mt-5 text-white/70 text-lg leading-relaxed max-w-2xl">
            Pick a starting point. We'll shape a plan that fits your goals and timeline on our discovery call. All packages are quoted and delivered by <strong className="text-white">APEX Innovation Solutions</strong>.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`relative p-7 md:p-8 rounded-3xl border backdrop-blur-xl overflow-hidden ${
                t.highlighted
                  ? "bg-white/[0.05] border-apex-orange/50 shadow-[0_0_60px_-10px_rgba(255,107,53,0.45)] md:scale-[1.03]"
                  : "bg-white/[0.03] border-white/10"
              }`}
              data-testid={`pricing-tier-${i}`}
            >
              {t.highlighted && (
                <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-apex-orange/15 border border-apex-orange/40 text-[11px] font-bold uppercase tracking-widest text-apex-orange">
                  <Sparkles className="w-3 h-3" /> Most popular
                </div>
              )}
              <div className="text-sm text-white/60 uppercase tracking-[0.24em]">{t.name}</div>
              <div className="mt-3 flex items-baseline gap-1.5">
                {t.priceUsd !== null && <span className="text-xs text-white/50">Starting from</span>}
              </div>
              <div className={`mt-1 font-heading font-extrabold text-4xl md:text-5xl ${t.highlighted ? "text-apex-orange" : ""}`} data-testid={`pricing-price-${i}`}>
                {t.priceUsd !== null ? format(t.priceUsd) : t.priceLabel}
              </div>
              {t.priceUsd !== null && (
                <div className="mt-1 text-[11px] uppercase tracking-widest text-white/40">
                  {currency === "USD" ? "USD" : "INR (approx.)"}
                </div>
              )}
              <p className="mt-3 text-sm text-white/70 leading-relaxed">{t.tagline}</p>

              <ul className="mt-6 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-white/85">
                    <span className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center ${t.highlighted ? "bg-apex-orange/15 border border-apex-orange/40" : "bg-apex-cyan/10 border border-apex-cyan/30"}`}>
                      <Check className={`w-3 h-3 ${t.highlighted ? "text-apex-orange" : "text-apex-cyan"}`} strokeWidth={2.5} />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                className={`mt-8 w-full block text-center rounded-full px-6 py-3.5 font-semibold transition-all duration-300 ${
                  t.highlighted
                    ? "bg-apex-orange text-white hover:bg-apex-orangeHover shadow-[0_10px_30px_-10px_rgba(255,107,53,0.6)]"
                    : "bg-white/[0.05] border border-white/15 text-white hover:bg-white/[0.1] hover:border-white/30"
                }`}
                data-testid={`pricing-cta-${i}`}
              >
                {t.cta}
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
